"use client";

import * as React from "react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type SizeRow = {
  us: string;
  uk: string;
  eu: string;
  cm: string;
  fit: string;
  matched: boolean;
};
const brands = [{
  id: "engraving",
  label: "Engraving"
}, {
  id: "laser",
  label: "Laser"
}, {
  id: "sublimation",
  label: "Sublimation"
}, {
  id: "embossing",
  label: "Embossing"
}];
const engravingRows: SizeRow[] = [{
  us: "0.5mm",
  uk: "0.8mm",
  eu: "1.0mm",
  cm: "Deep",
  fit: "Standard industrial depth",
  matched: false
}, {
  us: "1.0mm",
  uk: "1.2mm",
  eu: "1.5mm",
  cm: "Deep",
  fit: "High-contrast finish",
  matched: false
}, {
  us: "1.5mm",
  uk: "1.8mm",
  eu: "2.0mm",
  cm: "Deep",
  fit: "Recommended for durability",
  matched: true
}, {
  us: "2.0mm",
  uk: "2.2mm",
  eu: "2.5mm",
  cm: "Deep",
  fit: "Heavy-duty applications",
  matched: false
}, {
  us: "2.5mm",
  uk: "2.8mm",
  eu: "3.0mm",
  cm: "Deep",
  fit: "Maximum structural depth",
  matched: false
}];
const laserRows: SizeRow[] = [{
  us: "0.1mm",
  uk: "0.2mm",
  eu: "0.3mm",
  cm: "Fine",
  fit: "Precision marking",
  matched: false
}, {
  us: "0.2mm",
  uk: "0.3mm",
  eu: "0.4mm",
  cm: "Fine",
  fit: "Detailed vector work",
  matched: false
}, {
  us: "0.3mm",
  uk: "0.4mm",
  eu: "0.5mm",
  cm: "Fine",
  fit: "Standard laser depth",
  matched: true
}, {
  us: "0.4mm",
  uk: "0.5mm",
  eu: "0.6mm",
  cm: "Fine",
  fit: "High-speed etching",
  matched: false
}, {
  us: "0.5mm",
  uk: "0.6mm",
  eu: "0.7mm",
  cm: "Fine",
  fit: "Deep laser ablation",
  matched: false
}];
const sublimationRows: SizeRow[] = [{
  us: "100dpi",
  uk: "150dpi",
  eu: "200dpi",
  cm: "High",
  fit: "Standard resolution",
  matched: false
}, {
  us: "200dpi",
  uk: "250dpi",
  eu: "300dpi",
  cm: "High",
  fit: "Sharp image quality",
  matched: false
}, {
  us: "300dpi",
  uk: "350dpi",
  eu: "400dpi",
  cm: "High",
  fit: "Professional grade",
  matched: true
}, {
  us: "400dpi",
  uk: "450dpi",
  eu: "500dpi",
  cm: "High",
  fit: "Ultra-fine detail",
  matched: false
}, {
  us: "500dpi",
  uk: "550dpi",
  eu: "600dpi",
  cm: "High",
  fit: "Maximum clarity",
  matched: false
}];
const embossingRows: SizeRow[] = [{
  us: "0.2mm",
  uk: "0.3mm",
  eu: "0.4mm",
  cm: "Raised",
  fit: "Light texture",
  matched: false
}, {
  us: "0.4mm",
  uk: "0.5mm",
  eu: "0.6mm",
  cm: "Raised",
  fit: "Standard relief",
  matched: false
}, {
  us: "0.6mm",
  uk: "0.7mm",
  eu: "0.8mm",
  cm: "Raised",
  fit: "Optimal tactile feel",
  matched: true
}, {
  us: "0.8mm",
  uk: "0.9mm",
  eu: "1.0mm",
  cm: "Raised",
  fit: "Deep relief",
  matched: false
}, {
  us: "1.0mm",
  uk: "1.1mm",
  eu: "1.2mm",
  cm: "Raised",
  fit: "Heavy embossing",
  matched: false
}];
const brandData: Record<string, SizeRow[]> = {
  engraving: engravingRows,
  laser: laserRows,
  sublimation: sublimationRows,
  embossing: embossingRows
};
function SizeTable({
  rows
}: {
  rows: SizeRow[];
}) {
  return <TooltipProvider delayDuration={150}>
      <Table className="border-collapse">
        <TableHeader>
          <TableRow className="border-b border-[var(--border)] hover:bg-transparent">
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--card-foreground)] sticky left-0 bg-background">
              MIN
            </TableHead>
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--card-foreground)]">
              MID
            </TableHead>
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--card-foreground)]">
              MAX
            </TableHead>
            <TableHead className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--card-foreground)]">
              TYPE
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, i) => <Tooltip key={i}>
              <TooltipTrigger asChild>
                <TableRow data-index={i} className="border-b border-dashed border-[var(--border)] odd:bg-card/40 transition-colors duration-200 hover:bg-secondary/40 cursor-default">
                  <TableCell className={row.matched ? "sticky left-0 bg-primary text-primary-foreground font-mono font-semibold text-base" : "sticky left-0 bg-background font-mono text-base text-foreground"}>
                    {row.us}
                  </TableCell>
                  <TableCell className="font-mono text-base text-foreground">
                    {row.uk}
                  </TableCell>
                  <TableCell className="font-mono text-base text-foreground">
                    {row.eu}
                  </TableCell>
                  <TableCell className="font-mono text-base text-foreground">
                    {row.cm}
                  </TableCell>
                </TableRow>
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-primary text-primary-foreground font-mono text-[0.7rem] uppercase tracking-[0.12em]">
                {row.fit}
              </TooltipContent>
            </Tooltip>)}
        </TableBody>
      </Table>
    </TooltipProvider>;
}
export default function MarkingStandardsSpecifications() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1935" id="marking-standards-specifications" className="bg-background text-foreground py-24 md:py-36 px-5 md:px-10">
      <div className="mx-auto max-w-[1320px]">
        <div ref={ref} className={"relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-end transition-all duration-700 " + (isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6")}>
          <span aria-hidden="true" className="pointer-events-none absolute -top-10 left-0 select-none font-serif font-bold text-foreground/[0.06] leading-none text-[8rem] md:text-[12rem]">
            02
          </span>

          <div className="lg:col-span-8 relative z-10">
            <p className="font-mono text-xs md:text-sm uppercase tracking-[0.22em] text-[var(--card-foreground)] mb-6">
              § 02 / TECHNICAL SPECIFICATIONS
            </p>
            <h2 className="font-default font-semibold leading-[0.98] tracking-[-0.02em] text-[clamp(2rem,4.5vw,3.75rem)] text-foreground">
              Precision &middot; Depth &middot; Quality,{" "}
              <span className="relative inline-block">
                Measured
                <span className="absolute -bottom-1 left-0 h-[3px] w-full bg-primary" />
                <span className="absolute -bottom-1 right-[-6px] h-[7px] w-[7px] rounded-sm bg-primary" />
              </span>{" "}
              To Standard
            </h2>
            <p className="mt-6 max-w-xl font-default text-base md:text-lg leading-relaxed text-[var(--card-foreground)]">
              Review our technical marking standards across all production methods. Hover any row
              for specific engineering notes from our quality control team.
            </p>
          </div>

          <div className="lg:col-span-4 relative z-10">
            <Tabs defaultValue="engraving" className="w-full">
              <TabsList className="flex w-full flex-wrap h-auto gap-1 bg-secondary p-1 rounded-lg">
                {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="flex-1 font-mono text-xs uppercase tracking-[0.1em] data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md">
                    {brand.label}
                  </TabsTrigger>)}
              </TabsList>

              {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="mt-10 animate-in fade-in-0 duration-300">
                  <div className="block md:hidden">
                    <div className="rounded-lg border border-dashed border-[var(--border)] p-1">
                      <ScrollArea className="w-full whitespace-nowrap">
                        <div className="min-w-[480px] p-3">
                          <SizeTable rows={brandData[brand.id]} />
                        </div>
                        <ScrollBar orientation="horizontal" />
                      </ScrollArea>
                    </div>
                  </div>
                </TabsContent>)}
            </Tabs>
          </div>
        </div>

        <div className="my-10 hidden md:block border-t border-dashed border-[var(--border)]" />

        <div className="hidden md:block">
          <Tabs defaultValue="engraving" className="w-full">
            <TabsList className="mb-8 inline-flex h-auto gap-1 bg-secondary p-1 rounded-lg">
              {brands.map((brand, i) => <TabsTrigger key={brand.id} data-index={i} value={brand.id} className="font-mono text-xs uppercase tracking-[0.12em] px-5 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md">
                  {brand.label}
                </TabsTrigger>)}
            </TabsList>

            {brands.map((brand, i) => <TabsContent key={brand.id} data-index={i} value={brand.id} className="animate-in fade-in-0 duration-300">
                <div className="rounded-lg border border-dashed border-[var(--border)] p-4 md:p-6 bg-card/20">
                  <SizeTable rows={brandData[brand.id]} />
                </div>
                <p className="mt-4 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-[var(--card-foreground)]">
                  Gold row indicates the industry-standard specification for this
                  process
                </p>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}